/**
 * `Loading` — typeset loading, and the frame every other state shares.
 * `docs/design-direction.md` §8.8.
 *
 * **There is no spinner in this app and no shimmering skeleton** (§12.7). Loading is
 * three marks and nothing else:
 *
 *  1. the one-drop bar ruler with a single `--accent` tick advancing one position per
 *     `--t8` (the `Ruler` primitive's `loader` variant),
 *  2. a mono `--fs-13` status line in `--text-3` with a blinking 1ch block caret at
 *     `steps(1) var(--t4)`,
 *  3. the real 56px rows with everything replaced by 1px `--border` rules at 40% and
 *     70% widths, drawing in with `scaleX(0) → 1` from the left over `--t16`,
 *     staggered 24ms, with the halftone dot field drifting behind them — printing,
 *     not shimmering.
 *
 * **If the API returns real progress, it prints `042 / 128`. If it does not, nothing
 * numeric is printed at all** (§8.8, §12.6). {@link formatProgress} is the only place
 * that decision is made, it returns `null` for every partial answer, and its test is
 * the anti-fabrication regression: a loading screen that invents a counter is exactly
 * the same sin as a waveform synthesised from a hash.
 *
 * ## Why the frame lives here
 *
 * `StateFrame` is the single rule that makes the contract's central promise true:
 * **every state occupies the same grid as the content it replaces**, so nothing jumps
 * when the data arrives (§0's D2 graft, §13's checklist). It is one class list —
 * `frame` plus one grid class — for loading, empty, error, offline *and* the loaded
 * content; which of the five is showing is a `data-state` attribute, and no rule in
 * `states.module.css` changes a layout property based on it. Screens render their
 * loaded content through the same component.
 *
 * It is exported from this file rather than from a sixth module because T37's file
 * list is closed — one file, one owner — and loading is the first state any grid ever
 * shows. `Rule.tsx` sets the same precedent for `cx` in the primitives.
 */

import type { CSSProperties, JSX, ReactNode } from 'react';

import { Ruler } from '../../components/primitives/Ruler';
import { cx } from '../../components/primitives/Rule';

import styles from './states.module.css';

/* ══ the frame ═════════════════════════════════════════════════════════════════════ */

/**
 * The grids a screen region can occupy, from `design-direction.md` §5.2 and §8.
 *
 * | grid | where |
 * |---|---|
 * | `ledger` | the crate, history, a discography — 56px rows that own their own gutter |
 * | `plate` | Now Playing: one stack of modules with `--s6` between them (§8.1) |
 * | `shelf` | MY CRATE: two columns of disc-over-sleeve items (§8.5) |
 * | `index` | the drawer index: dense two-column, 28px rows (§8.3) |
 * | `sheet` | the SET LIST paper sheet, which owns its own edges (§8.4) |
 * | `plain` | anything else: a single gutter-padded column |
 */
export const STATE_GRIDS = ['ledger', 'plate', 'shelf', 'index', 'sheet', 'plain'] as const;

export type StateGrid = (typeof STATE_GRIDS)[number];

/** Which of the five states a frame is currently showing. */
export type StateKind = 'loading' | 'empty' | 'error' | 'offline' | 'content';

export interface StateFrameProps {
  readonly grid: StateGrid;
  /** Default `content`: a screen rendering its real data uses the same frame. */
  readonly state?: StateKind;
  readonly children?: ReactNode;
  /**
   * Extra classes for the frame. **Anything that changes the layout belongs to the
   * grid, not here** — a screen that styles its loading frame differently from its
   * loaded frame has broken the one promise this component exists to keep.
   */
  readonly className?: string | undefined;
  /** `role="alert"` for the error plate, `role="status"` for a live region. */
  readonly role?: 'alert' | 'status' | 'group' | undefined;
  readonly ariaLabel?: string | undefined;
  /** Forwarded to the frame element, for a screen that needs a scroll anchor. */
  readonly id?: string | undefined;
}

/** The grid a state and its content share. */
export function StateFrame({
  grid,
  state = 'content',
  children,
  className,
  role,
  ariaLabel,
  id,
}: StateFrameProps): JSX.Element {
  return (
    <section
      className={cx(styles.frame, styles[grid], className)}
      data-grid={grid}
      data-state={state}
      role={role}
      aria-label={ariaLabel}
      id={id}
    >
      <div className={styles.body}>{children}</div>
    </section>
  );
}

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/**
 * The default status line.
 *
 * Sentence case, deliberately: the audience is Russian-speaking and tracked uppercase
 * Cyrillic reads as shouting, so a Cyrillic string never enters the mono tier in caps
 * (§4.3, §12.16). The tier survives — mono, `--fs-13`, `--text-3` — and `.cyr` drops
 * the tracking and the forced uppercase, exactly as the primitives do.
 */
export const LOADING_STATUS_RU = 'Читаю ящик';

/* ══ progress ══════════════════════════════════════════════════════════════════════ */

/**
 * `042 / 128`, or `null`.
 *
 * Returns a string **only** when both numbers are real, finite, non-negative and
 * consistent. Every other input — a missing total, a `NaN`, a negative, a processed
 * count past the total — returns `null`, and the caller prints nothing numeric. There
 * is no percentage form and no "estimating…" form, because both would be inventions
 * (§8.8, §12.6).
 */
export function formatProgress(
  processed: number | null | undefined,
  total: number | null | undefined,
): string | null {
  if (typeof processed !== 'number' || typeof total !== 'number') return null;
  if (!Number.isFinite(processed) || !Number.isFinite(total)) return null;
  if (processed < 0 || total <= 0 || processed > total) return null;
  return `${String(Math.round(processed)).padStart(3, '0')} / ${String(Math.round(total))}`;
}

/* ══ Loading ═══════════════════════════════════════════════════════════════════════ */

/** How many skeleton rows carry the 24ms stagger before the rest appear at once. */
const STAGGER_CAP = 12;

export interface LoadingProps {
  /** The grid the arriving content will occupy. */
  readonly grid: StateGrid;
  /** The mono status line. Russian, sentence case. */
  readonly status?: string;
  /**
   * How many 56px row skeletons to draw. `0` (the default) is the bare ruler + status
   * line, which is the right global state for a screen that is not a list.
   */
  readonly rows?: number;
  /** Real progress from the API, or `null`/omitted. Never estimated here. */
  readonly processed?: number | null | undefined;
  readonly total?: number | null | undefined;
  readonly className?: string | undefined;
}

export function Loading({
  grid,
  status = LOADING_STATUS_RU,
  rows = 0,
  processed,
  total,
  className,
}: LoadingProps): JSX.Element {
  const progress = formatProgress(processed, total);
  const count = Number.isFinite(rows) ? Math.max(0, Math.trunc(rows)) : 0;

  return (
    <StateFrame grid={grid} state="loading" className={className}>
      <div className={styles.status}>
        {/* The loader is the ruler advancing. Its `role="status"` and its
            screen-reader label come from the primitive; the visible line is the
            screen's own copy (§6.6 job 3, §8.8). */}
        <Ruler variant="loader" label={status} />
        <span className={cx('t-mono', 't-13', styles.cyr)} data-state-string="loading">
          {status}
        </span>
        <span className={styles.caret} aria-hidden="true" />
        {progress === null ? null : (
          <span className={cx('t-mono', 't-13', styles.progress)} data-progress="true">
            {progress}
          </span>
        )}
      </div>

      {count === 0 ? null : (
        <div className={cx('halftone', styles.drift)} aria-hidden="true" data-skeletons="true">
          {Array.from({ length: count }, (_unused, index) => (
            <div
              key={index}
              className={styles.skeleton}
              style={{ '--i': String(Math.min(index, STAGGER_CAP - 1)) } as CSSProperties}
            >
              <span className={styles.chip} />
              <span className={styles.lines}>
                <span className={cx(styles.bar, styles.barTitle)} />
                <span className={cx(styles.bar, styles.barMeta)} />
              </span>
            </div>
          ))}
        </div>
      )}
    </StateFrame>
  );
}
